import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Save, Send, RotateCcw, Plus, Eye, Trash2 } from "lucide-react";

export default function AdminEmailTemplates() {
  const [items, setItems] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [draft, setDraft] = useState({ subject: "", html: "" });
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [saving, setSaving] = useState(false);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [newTpl, setNewTpl] = useState({ key: "", label: "", subject: "", html: "", placeholders: "name" });

  const selected = useMemo(() => items.find((t) => t.key === selectedKey), [items, selectedKey]);

  const load = async () => {
    const { data } = await api.get("/admin/email-templates");
    setItems(data.items || []);
    if (!selectedKey && data.items?.length) setSelectedKey(data.items[0].key);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selected) return;
    setDraft({ subject: selected.subject || "", html: selected.html || "" });
    doPreview(selected.key);
    // eslint-disable-next-line
  }, [selectedKey, selected?.updated_at, selected?.has_override]);

  const doPreview = async (key) => {
    try {
      const { data } = await api.post(`/admin/email-templates/${key}/preview`);
      setPreviewHtml(data.html);
      setPreviewSubject(data.subject);
    } catch { /* keep last preview */ }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/admin/email-templates/${selected.key}`, {
        subject: draft.subject,
        html: draft.html,
      });
      toast.success(`Saved — ${selected.label}`);
      await load();
      await doPreview(selected.key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const revertToDefault = async () => {
    if (!selected || !selected.has_override) return;
    if (!window.confirm(`Revert "${selected.label}" back to the shipped default?`)) return;
    try {
      await api.delete(`/admin/email-templates/${selected.key}`);
      toast.success("Reverted to default");
      await load();
      await doPreview(selected.key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteCustom = async () => {
    if (!selected || !selected.is_custom) return;
    if (!window.confirm(`Delete custom template "${selected.key}" permanently?`)) return;
    try {
      await api.delete(`/admin/email-templates/${selected.key}`);
      toast.success("Deleted");
      setSelectedKey(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a recipient email first");
    try {
      await api.post(`/admin/email-templates/${selected.key}/test`, { to: testTo, sender: "admin" });
      toast.success(`Test email sent to ${testTo}`);
      setTestModalOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const createCustom = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/email-templates", {
        key: newTpl.key,
        label: newTpl.label || newTpl.key,
        subject: newTpl.subject,
        html: newTpl.html,
        placeholders: newTpl.placeholders.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Template created");
      setNewModalOpen(false);
      setSelectedKey(newTpl.key);
      setNewTpl({ key: "", label: "", subject: "", html: "", placeholders: "name" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="overline text-safety">Admin</div>
          <h1 className="mt-1 font-display font-black text-3xl tracking-tighter text-forest">Email templates</h1>
          <p className="mt-1 text-sm text-muted-foreground">Change the subject and body of every outbound email. Live preview + send-to-me test.</p>
        </div>
        <Button onClick={() => setNewModalOpen(true)} data-testid="tpl-new-btn" className="rounded-none bg-safety hover:bg-safety/90 text-white h-11">
          <Plus className="w-4 h-4 mr-2" /> New custom template
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[280px,1fr,1fr] gap-4">
        {/* List */}
        <aside className="border border-border bg-white h-fit">
          {items.map((t) => {
            const active = t.key === selectedKey;
            return (
              <button key={t.key} onClick={() => setSelectedKey(t.key)}
                data-testid={`tpl-row-${t.key}`}
                className={`w-full text-left p-3 border-b border-border last:border-b-0 transition-colors ${active ? "bg-sage-light" : "hover:bg-sage-light/50"}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Mail className="w-3.5 h-3.5 text-forest" />
                  <span className="text-sm font-display text-forest truncate">{t.label}</span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  <code className="text-[10px] bg-muted px-1.5 py-0.5">{t.key}</code>
                  {t.is_custom && <Badge className="rounded-none bg-safety text-white overline text-[8px]">custom</Badge>}
                  {t.has_override && !t.is_custom && <Badge className="rounded-none bg-forest text-white overline text-[8px]">overridden</Badge>}
                </div>
              </button>
            );
          })}
        </aside>

        {/* Editor */}
        <section className="border border-border bg-white p-5 min-w-0" data-testid="tpl-editor">
          {selected ? (
            <>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="overline text-safety text-[10px]">{selected.is_custom ? "Custom" : "Built-in"}</div>
                  <div className="font-display text-lg text-forest">{selected.label}</div>
                </div>
                <div className="flex gap-1">
                  {selected.has_override && !selected.is_custom && (
                    <Button onClick={revertToDefault} size="sm" variant="outline" data-testid="tpl-revert-btn" className="rounded-none">
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revert
                    </Button>
                  )}
                  {selected.is_custom && (
                    <Button onClick={deleteCustom} size="sm" variant="outline" data-testid="tpl-delete-btn" className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  )}
                </div>
              </div>

              <div className="text-xs text-muted-foreground mb-3">
                Placeholders: {selected.placeholders?.map((p) => (
                  <code key={p} className="mx-0.5 bg-muted px-1.5 py-0.5">{`{${p}}`}</code>
                ))}
              </div>

              <div className="space-y-4">
                <div>
                  <div className="overline text-muted-foreground mb-1.5 text-[10px]">Subject</div>
                  <Input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} data-testid="tpl-subject" className="rounded-none h-11" />
                </div>
                <div>
                  <div className="overline text-muted-foreground mb-1.5 text-[10px] flex items-center justify-between">
                    <span>Body HTML (wrapped in brand shell automatically)</span>
                    <button onClick={() => doPreview(selected.key)} className="text-forest hover:underline flex items-center gap-1">
                      <Eye className="w-3 h-3" /> Refresh preview
                    </button>
                  </div>
                  <Textarea rows={16} value={draft.html} onChange={(e) => setDraft({ ...draft, html: e.target.value })} data-testid="tpl-html" placeholder={"Leave blank to keep shipped default HTML.\nUse {name}, {reset_url}, {site_url} etc."} className="rounded-none font-mono text-xs" />
                </div>
              </div>

              <div className="pt-4 mt-4 border-t border-border flex justify-between flex-wrap gap-2">
                <Button onClick={() => setTestModalOpen(true)} variant="outline" size="sm" data-testid="tpl-send-test-btn" className="rounded-none">
                  <Send className="w-3.5 h-3.5 mr-1" /> Send test email
                </Button>
                <Button onClick={save} disabled={saving} data-testid="tpl-save-btn" className="rounded-none bg-forest hover:bg-forest-800 text-white h-10">
                  <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save & preview"}
                </Button>
              </div>
            </>
          ) : (
            <div className="p-10 text-center text-sm text-muted-foreground">Select a template to edit.</div>
          )}
        </section>

        {/* Preview */}
        <section className="border border-border bg-white min-w-0" data-testid="tpl-preview">
          <div className="p-3 border-b border-border bg-sage-light">
            <div className="overline text-[10px] text-muted-foreground">Live preview · subject</div>
            <div className="text-sm font-display truncate">{previewSubject || "—"}</div>
          </div>
          <iframe title="preview" srcDoc={previewHtml} className="w-full bg-white" style={{ height: 600, border: 0 }} />
        </section>
      </div>

      {/* Test send modal */}
      <Dialog open={testModalOpen} onOpenChange={setTestModalOpen}>
        <DialogContent className="bg-white rounded-none">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Send test email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">This will send the current SAVED version of <code className="bg-muted px-1">{selected?.key}</code> to any email using sample data.</p>
            <div>
              <div className="overline text-muted-foreground mb-2 text-[10px]">Recipient</div>
              <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="tpl-test-to" placeholder="you@yourcompany.com" className="rounded-none h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestModalOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={sendTest} data-testid="tpl-test-send-btn" className="rounded-none bg-forest hover:bg-forest-800 text-white">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New template modal */}
      <Dialog open={newModalOpen} onOpenChange={setNewModalOpen}>
        <DialogContent className="bg-white rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">New custom template</DialogTitle></DialogHeader>
          <form onSubmit={createCustom} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="overline text-muted-foreground mb-2 text-[10px]">Key (used in code)</div>
                <Input required value={newTpl.key} onChange={(e) => setNewTpl({ ...newTpl, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                       placeholder="spring_promo_2026" data-testid="tpl-new-key" className="rounded-none h-11 font-mono text-xs" />
              </div>
              <div>
                <div className="overline text-muted-foreground mb-2 text-[10px]">Label (shown in admin)</div>
                <Input value={newTpl.label} onChange={(e) => setNewTpl({ ...newTpl, label: e.target.value })} placeholder="Spring 2026 promo blast" data-testid="tpl-new-label" className="rounded-none h-11" />
              </div>
            </div>
            <div>
              <div className="overline text-muted-foreground mb-2 text-[10px]">Placeholders (comma-separated)</div>
              <Input value={newTpl.placeholders} onChange={(e) => setNewTpl({ ...newTpl, placeholders: e.target.value })} placeholder="name, offer_code, discount" data-testid="tpl-new-placeholders" className="rounded-none h-11 font-mono text-xs" />
            </div>
            <div>
              <div className="overline text-muted-foreground mb-2 text-[10px]">Subject</div>
              <Input required value={newTpl.subject} onChange={(e) => setNewTpl({ ...newTpl, subject: e.target.value })} placeholder="Hi {name} — spring offer inside" data-testid="tpl-new-subject" className="rounded-none h-11" />
            </div>
            <div>
              <div className="overline text-muted-foreground mb-2 text-[10px]">Body HTML (wrapped in brand shell)</div>
              <Textarea required rows={8} value={newTpl.html} onChange={(e) => setNewTpl({ ...newTpl, html: e.target.value })}
                        placeholder={"<p>Hi {name},</p><p>Use code {offer_code} for {discount}% off…</p>"}
                        data-testid="tpl-new-html" className="rounded-none font-mono text-xs" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewModalOpen(false)} className="rounded-none">Cancel</Button>
              <Button type="submit" data-testid="tpl-new-create-btn" className="rounded-none bg-safety hover:bg-safety/90 text-white">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
